import { useState } from 'react';
import { Coins } from 'lucide-react';
import { Button } from '../ui';
import { BuyCreditsModal } from '../../settings/shared';
import { useCreditsWidgetCta } from '../../../lib/use-credits-widget-cta';
import { useTranslation } from '../../../lib/i18n';
import type { VerifyPhoneOffer } from '../../../lib/use-credits-widget-state';

// The copy-key mapping lives with the pure state logic (and is unit-tested there); re-exported here so
// the three widget components keep importing their shared pieces from one place.
export { creditsWidgetCopyKeys } from '../../../lib/use-credits-widget-state';

/**
 * The low/out-of-credits widgets' shared pieces — the icon and the one CTA button — so the drawer card,
 * the inner-screen banner and the in-chat card render the same glyph and action. Only the LAYOUT
 * differs per surface (each of those three files owns its own arrangement).
 */

/** The 16px Lucide "coins" glyph the frames use (16346:94987 `16/coins`), inheriting text colour. */
export function CreditsWidgetIcon({ size = 16 }: { size?: number }) {
  return <Coins size={size} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />;
}

/**
 * The widgets' single button — "Upgrade" (free/daily) or "Upgrade Plan" (paid/monthly), both to the
 * picker, or "Add Credits" on the Max+ top tier (the top-up modal). It reuses the drawer card's own
 * action via `useCreditsWidgetCta`, so all three surfaces send the user to the same place and the
 * top-up modal is wired identically.
 *
 * Returns null only when the CTA is unavailable — which coincides with `useCreditsWidgetState` being
 * `none`, so a rendered widget always has a button.
 */
export function CreditsWidgetCtaButton({
  fullWidth = false,
  size = 'small',
  label,
}: {
  fullWidth?: boolean;
  size?: 'small' | 'tiny';
  /** Override the button copy — the credits card passes the personal-sale "Upgrade and save 30%" label
   *  here while the offer is live, so the swap stays scoped to that card and doesn't touch the other
   *  surfaces this shared button serves (the inner-screen banner, the in-chat card). */
  label?: string;
}) {
  const cta = useCreditsWidgetCta();
  const [buyOpen, setBuyOpen] = useState(false);
  if (!cta) return null;
  const buttonLabel = label ?? cta.label;

  // Two arms, mirroring the drawer card (`AccountCard`): the `packages` arm stays a real <a> so
  // cmd-click / open-in-new-tab reach the picker; the `topup` arm must NOT be a link — it opens a
  // modal in place, and an href would let a middle-click navigate to the picker instead.
  const button =
    cta.kind === 'topup' ? (
      <Button
        color="accent"
        size={size}
        fullWidth={fullWidth}
        onClick={() => {
          cta.onClick();
          setBuyOpen(true);
        }}
      >
        {buttonLabel}
      </Button>
    ) : (
      <Button
        color="accent"
        size={size}
        fullWidth={fullWidth}
        href={cta.href ?? undefined}
        linkAs={cta.path ?? undefined}
        onClick={cta.onClick}
      >
        {buttonLabel}
      </Button>
    );

  return (
    <>
      {button}
      {cta.buyCredits && (
        <BuyCreditsModal
          open={buyOpen}
          onClose={() => setBuyOpen(false)}
          topup={cta.buyCredits.topup}
          planName={cta.buyCredits.planName}
          checkoutHref={cta.buyCredits.checkoutHref}
        />
      )}
    </>
  );
}

/**
 * The widget's action row. Ordinarily just the CTA button, exactly as before. On the daily-limit wall's
 * phone offer (`verifyPhoneOffer`) it adds "Verify your phone" as the SECONDARY (outlined) button, while
 * Upgrade keeps its primary style (Ronny's call, 2026-10-31). Two arrangements, the same on all three
 * surfaces:
 *
 *  - `stack` (chat inline card, drawer/menu card): Upgrade full width, then Verify full width under it.
 *  - `row` (sticky bar, desktop banner): [Verify] [Upgrade], Upgrade at the trailing edge, single-line
 *    labels.
 *
 * Every surface that renders the daily out state goes through this, so the offer cannot reach one and
 * miss another.
 */
export function CreditsWidgetActions({
  offer,
  onVerify,
  layout,
  fullWidth = false,
  size = 'small',
  label,
}: {
  offer: VerifyPhoneOffer | null;
  onVerify: () => void;
  layout: 'stack' | 'row';
  /** Without an offer: whether the lone CTA spans the width (unchanged per-surface behaviour). */
  fullWidth?: boolean;
  size?: 'small' | 'tiny';
  label?: string;
}) {
  const { t } = useTranslation();
  if (!offer) return <CreditsWidgetCtaButton fullWidth={fullWidth} size={size} label={label} />;
  const verify = (
    <Button variant="outlined" color="standard" size={size} fullWidth={layout === 'stack'} onClick={onVerify}>
      <span style={{ whiteSpace: 'nowrap' }}>{t('credits.widget.cta.verifyPhone')}</span>
    </Button>
  );
  if (layout === 'stack') {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, width: '100%' }}>
        <CreditsWidgetCtaButton fullWidth size={size} label={label} />
        {verify}
      </span>
    );
  }
  return (
    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8, whiteSpace: 'nowrap' }}>
      {verify}
      <CreditsWidgetCtaButton size={size} label={label} />
    </span>
  );
}
